import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Send, MessageSquare, HelpCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "../components/Footer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactUsPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/v2/contact`, formData);
      toast.success(response.data.message || "Message sent! We'll get back to you within 24-48 hours.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <img src="/images/gigseasy-logo.svg" alt="GigsEasy" className="h-10 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Contact Us</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Have questions or need help? Fill out the form below and our team will get back to you 
            within 24-48 hours.
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-lg flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Response Time</h3>
              <p className="text-sm text-slate-600">
                We typically respond within 24-48 hours during business days. 
                For urgent payment issues, please include "URGENT" in your subject line.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-lg flex items-center justify-center flex-shrink-0">
              <HelpCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Check Our FAQ First</h3>
              <p className="text-sm text-slate-600">
                Many common questions are answered in our FAQ section. 
                <Link to="/#faq" className="text-[#00B894] hover:underline ml-1">View FAQ</Link>
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="w-6 h-6 text-[#00B894]" />
            <h2 className="text-2xl font-semibold text-slate-900">Send Us a Message</h2>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">Your Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject" className="text-slate-700">Subject *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="How can we help you?"
                className="h-12 border-2 border-slate-200 focus:border-[#00B894] rounded-xl"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-slate-700">Message *</Label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Please describe your question or issue in detail..."
                rows={6}
                className="w-full px-4 py-3 border-2 border-slate-200 focus:border-[#00B894] focus:outline-none rounded-xl resize-none"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-[#00B894] to-[#0984E3] text-white font-medium rounded-full shadow-lg shadow-[#00B894]/25 hover:opacity-90 transition-all"
            >
              {loading ? "Sending..." : "Send Message"}
              <Send className="ml-2 w-5 h-5" />
            </Button>
          </form>
        </div>

        {/* Advertiser CTA */}
        <div className="mt-10 p-6 bg-gradient-to-br from-[#00B894]/10 to-[#0984E3]/10 rounded-2xl text-center">
          <h3 className="font-semibold text-slate-900 mb-2">Looking to Advertise or Create Tasks?</h3>
          <p className="text-slate-600 mb-4">
            Access our global on-demand workforce for your business needs.
          </p>
          <Link to="/advertise">
            <Button className="bg-[#00B894] hover:bg-[#00a383] text-white">
              Advertise With Us
            </Button>
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
